import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Search, Activity, MapPin, Target, AlertCircle, Video, Zap, Scan, Dumbbell, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import FavoriteButton from "@/components/FavoriteButton";
import ShareButton from "@/components/ShareButton";
import LRInfo from "@/components/LRInfo";
import { useLang } from "@/context/LanguageContext";
import { computeLR, fmtLR, lrPosColor, lrNegColor } from "@/lib/lr";

const POSITIONS = ["Alla", "Stående", "Sittande", "Ryggliggande", "Magliggande", "Sidliggande"];
const POS_EN = {
  "Stående": "Standing",
  "Sittande": "Seated",
  "Ryggliggande": "Supine",
  "Magliggande": "Prone",
  "Sidliggande": "Side-lying",
};

// Regions that present a sub-category picker before showing tests.
const SUBREGION_CONFIG = {
  reflexer: [
    { id: "reflexer", labelKey: "sub.reflexer", icon: Zap },
    { id: "dermatom", labelKey: "sub.dermatom", icon: Scan },
    { id: "myotom",   labelKey: "sub.myotom",   icon: Dumbbell },
  ],
};

export default function Region() {
  const { t: tr, lang } = useLang();
  const { regionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [pos, setPos] = useState("Alla");
  const [selectedTest, setSelectedTest] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/regions/${regionId}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error(tr("region.errLoad")))
      .finally(() => setLoading(false));
  }, [regionId, lang, tr]);

  // Auto-open dialog when ?test=<id> is present in URL (deep link from Share)
  useEffect(() => {
    if (!data) return;
    const tid = searchParams.get("test");
    if (tid) {
      const match = data.tests.find((t) => t.id === tid);
      if (match) setSelectedTest(match);
    }
  }, [data, searchParams]);

  // Sync dialog close → strip the query param
  const closeDialog = () => {
    setSelectedTest(null);
    if (searchParams.get("test")) {
      const next = new URLSearchParams(searchParams);
      next.delete("test");
      setSearchParams(next, { replace: true });
    }
  };

  const tests = useMemo(() => {
    if (!data) return [];
    const posTarget = pos === "Alla" ? null : (lang === "en" ? POS_EN[pos] : pos);
    const subFilter = searchParams.get("sub");
    return data.tests.filter((t) => {
      const matchQ =
        !query ||
        t.name.toLowerCase().includes(query.toLowerCase()) ||
        t.purpose.toLowerCase().includes(query.toLowerCase());
      const matchP = posTarget === null || t.position === posTarget;
      const matchSub = !subFilter || t.subregion === subFilter;
      return matchQ && matchP && matchSub;
    });
  }, [data, query, pos, lang, searchParams]);

  if (loading) {
    return <div className="text-muted-foreground" data-testid="region-loading">{tr("common.loading")}</div>;
  }

  if (!data) return null;

  const { region } = data;
  const subConfig = SUBREGION_CONFIG[regionId];
  const activeSub = searchParams.get("sub");

  // Sub-region picker view (e.g. /regioner/reflexer with no ?sub)
  if (subConfig && !activeSub) {
    const counts = {};
    data.tests.forEach((t) => { counts[t.subregion] = (counts[t.subregion] || 0) + 1; });
    return (
      <div data-testid="region-page">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6" data-testid="back-link">
          <ArrowLeft className="size-4" /> {tr("common.backToOverview")}
        </Link>

        <header className="mb-8 rise">
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3 font-mono">
            <span className="title-rule" />{tr("common.region")} · {data.tests.length} {tr("common.tests")}
          </div>
          <h1 className="font-serif text-3xl sm:text-5xl font-medium tracking-tight">{region.name}</h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">{region.description}</p>
        </header>

        <p className="text-sm text-muted-foreground mb-5 font-mono uppercase tracking-widest text-[11px]">
          <span className="title-rule" />{tr("sub.pickCategory")}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="subregion-grid">
          {subConfig.map((sc) => {
            const Icon = sc.icon;
            const next = new URLSearchParams(searchParams);
            next.set("sub", sc.id);
            return (
              <Link
                key={sc.id}
                to={`/regioner/${regionId}?${next.toString()}`}
                data-testid={`sub-card-${sc.id}`}
                className="group"
              >
                <Card className="p-6 hover:border-accent/60 hover:shadow-sm transition-all bg-card h-full">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="size-11 rounded-md bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <Icon className="size-5" />
                    </div>
                    <Badge variant="secondary" className="font-mono text-[10px]">
                      {counts[sc.id] || 0} {tr("common.tests")}
                    </Badge>
                  </div>
                  <h3 className="font-serif text-xl font-medium mb-1 group-hover:text-primary transition-colors">
                    {tr(sc.labelKey)}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {tr(`${sc.labelKey}.desc`)}
                  </p>
                  <div className="mt-3 text-[11px] font-mono uppercase tracking-widest text-muted-foreground group-hover:text-accent inline-flex items-center gap-1">
                    {tr("common.open")} <ChevronRight className="size-3" />
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="region-page">
      <Link
        to={subConfig ? `/regioner/${regionId}` : "/"}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6"
        data-testid="back-link"
      >
        <ArrowLeft className="size-4" /> {subConfig ? tr("sub.backToCategories") : tr("common.backToOverview")}
      </Link>

      <header className="mb-8 rise">
        <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3 font-mono">
          <span className="title-rule" />{tr("common.region")} · {data.tests.length} {tr("common.tests")}
        </div>
        <h1 className="font-serif text-3xl sm:text-5xl font-medium tracking-tight">
          {region.name}
          {activeSub && (
            <span className="text-muted-foreground"> · <em className="not-italic text-accent">{tr(`sub.${activeSub}`)}</em></span>
          )}
        </h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">{region.description}</p>
      </header>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={tr("common.search")} data-testid="search-input" className="pl-9 bg-card" />
        </div>
        <Select value={pos} onValueChange={setPos}>
          <SelectTrigger className="sm:w-48 bg-card" data-testid="position-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {POSITIONS.map((p) => (
              <SelectItem key={p} value={p} data-testid={`pos-${p}`}>{p === "Alla" ? tr("pos.all") : tr(`pos.${p}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {tests.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="no-results">{tr("region.noResults")}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tests-grid">
          {tests.map((t, i) => (
            <Card
              key={t.id}
              data-testid={`test-card-${t.id}`}
              className={`group p-5 cursor-pointer hover:border-accent/60 hover:shadow-sm transition-all bg-card rise rise-${(i % 5) + 1}`}
              onClick={() => setSelectedTest(t)}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-serif text-lg font-medium leading-tight group-hover:text-primary transition-colors flex-1">
                  {t.name}
                </h3>
                <div className="flex items-center gap-1 shrink-0">
                  <Badge variant="secondary" className="text-[10px] font-mono">
                    {t.position}
                  </Badge>
                  <ShareButton regionId={regionId} testId={t.id} testName={t.name} />
                  <FavoriteButton testId={t.id} />
                </div>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2">{t.purpose}</p>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!selectedTest} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-2xl bg-card max-h-[90vh] overflow-y-auto" data-testid="test-dialog">
          {selectedTest && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] font-mono">{selectedTest.position}</Badge>
                    <Badge variant="outline" className="text-[10px] font-mono">{region.short}</Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <ShareButton regionId={regionId} testId={selectedTest.id} testName={selectedTest.name} size="lg" />
                    <FavoriteButton testId={selectedTest.id} size="lg" />
                  </div>
                </div>
                <DialogTitle className="font-serif text-2xl tracking-tight">{selectedTest.name}</DialogTitle>
              </DialogHeader>

              {/* Text alltid synlig först */}
              <div className="space-y-5 mt-2">
                <Section icon={Target} label={tr("test.purpose")} text={selectedTest.purpose} testid="test-purpose" />
                <Section icon={Activity} label={tr("test.execution")} text={selectedTest.execution} testid="test-execution" />
                <Section icon={AlertCircle} label={tr("test.positive")} text={selectedTest.positive} testid="test-positive" accent>
                  {(selectedTest.sensitivity != null || selectedTest.specificity != null) && (() => {
                    const { lrPos, lrNeg } = computeLR(selectedTest.sensitivity, selectedTest.specificity);
                    return (
                      <div className="mt-2.5 text-xs font-mono space-y-0.5" data-testid="test-metrics">
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-muted-foreground">
                          {selectedTest.sensitivity != null && (
                            <span>
                              <span className="text-foreground">{tr("test.sens")}</span> {selectedTest.sensitivity}%
                            </span>
                          )}
                          {selectedTest.specificity != null && (
                            <span>
                              <span className="text-foreground">{tr("test.spec")}</span> {selectedTest.specificity}%
                            </span>
                          )}
                          {lrPos != null && (
                            <span title={tr("test.lrPosTip")}>
                              <span className="text-foreground">LR+:</span>{" "}
                              <span className={lrPosColor(lrPos)}>{fmtLR(lrPos)}</span>
                            </span>
                          )}
                          {lrNeg != null && (
                            <span title={tr("test.lrNegTip")}>
                              <span className="text-foreground">LR–:</span>{" "}
                              <span className={lrNegColor(lrNeg)}>{fmtLR(lrNeg)}</span>
                              <LRInfo />
                            </span>
                          )}
                        </div>
                        {selectedTest.source && (
                          <div className="text-[10px] text-muted-foreground/80 not-italic">
                            {tr("test.source")} {selectedTest.source}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </Section>
                {selectedTest.comment && (
                  <Section icon={MapPin} label={tr("test.comment")} text={selectedTest.comment} testid="test-comment" />
                )}
              </div>

              {/* Video embed (om finns) */}
              {selectedTest.video_url && (
                <div className="relative aspect-video rounded-md overflow-hidden bg-muted border border-border mt-5" data-testid="test-video">
                  <iframe
                    src={selectedTest.video_url}
                    className="w-full h-full"
                    title={`Video ${selectedTest.name}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                  <div className="absolute top-2 left-2 bg-background/85 backdrop-blur-sm rounded px-2 py-1 text-[10px] font-mono uppercase tracking-wider flex items-center gap-1 pointer-events-none">
                    <Video className="size-3" /> {tr("test.video")}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ icon: Icon, label, text, accent, testid, children }) {
  // Detect numbered steps like "1. Foo. 2. Bar. 3. Baz." and render as list
  const isNumberedSteps = /^\s*1\.\s.+?(?:\s+2\.\s)/.test(text || "");
  const steps = isNumberedSteps
    ? (text.match(/\d+\.\s+[^]*?(?=\s+\d+\.\s|$)/g) || []).map((s) => s.replace(/^\d+\.\s+/, "").trim())
    : null;

  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
        <Icon className={`size-3.5 ${accent ? "text-accent" : ""}`} />
        {label}
      </div>
      {steps ? (
        <ol className={`text-sm leading-relaxed space-y-1.5 ${accent ? "font-medium text-foreground" : "text-foreground/85"}`}>
          {steps.map((s, i) => (
            <li key={i} className="flex gap-2.5">
              <span className="font-mono text-muted-foreground tabular-nums shrink-0 min-w-[1.25rem]">{i + 1}.</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className={`text-sm leading-relaxed ${accent ? "font-medium text-foreground" : "text-foreground/85"}`}>
          {text}
        </p>
      )}
      {children}
    </div>
  );
}
